/**
 * Rule Violation Store — detects, logs, and tracks emotional/behavioral trading violations.
 * Syncs with Supabase when authenticated.
 */

import type { StoredTrade } from "./tradeStore";
import { getTrades } from "./tradeStore";
import { getCurrentUserId, dbInsert, dbSelect, dbUpdate } from "./dbSync";

const STORAGE_KEY = "mm-violations";
const listeners = new Set<() => void>();
let cachedRaw: string | null = null;
let cachedSnapshot: Violation[] = [];

export type ViolationType =
  | "fomo"
  | "revenge"
  | "overtrading"
  | "emotional_entry"
  | "risk_exceeded"
  | "no_stop_loss"
  | "moved_stop"
  | "early_exit";

export interface Violation {
  id: string;
  type: ViolationType;
  severity: "low" | "medium" | "high" | "critical";
  tradeId: string | null;
  instrument: string;
  description: string;
  detectedAt: string;
  date: string;
  acknowledged: boolean;
}

export const VIOLATION_META: Record<ViolationType, { label: string; emoji: string; color: string }> = {
  fomo: { label: "FOMO Entry", emoji: "😫", color: "text-warning" },
  revenge: { label: "Revenge Trade", emoji: "😤", color: "text-destructive" },
  overtrading: { label: "Overtrading", emoji: "🔥", color: "text-destructive" },
  emotional_entry: { label: "Emotional Entry", emoji: "😰", color: "text-warning" },
  risk_exceeded: { label: "Risk Exceeded", emoji: "⚠️", color: "text-destructive" },
  no_stop_loss: { label: "No Stop Loss", emoji: "🚫", color: "text-destructive" },
  moved_stop: { label: "Moved Stop Loss", emoji: "↔️", color: "text-warning" },
  early_exit: { label: "Early Exit", emoji: "🏃", color: "text-warning" },
};

function readLocal(): Violation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedSnapshot = raw ? JSON.parse(raw) : [];
    }
    return cachedSnapshot;
  } catch {
    return [];
  }
}

function writeLocal(violations: Violation[]) {
  const json = JSON.stringify(violations);
  cachedRaw = json;
  cachedSnapshot = violations;
  localStorage.setItem(STORAGE_KEY, json);
  listeners.forEach((fn) => fn());
}

export async function syncViolationsFromDB() {
  if (!getCurrentUserId()) return;
  const rows = await dbSelect<any>("violations", "detected_at");
  if (rows.length > 0) {
    const violations: Violation[] = rows.map((r: any) => ({
      id: r.id,
      type: r.type as ViolationType,
      severity: r.severity,
      tradeId: r.trade_id,
      instrument: r.instrument,
      description: r.description,
      detectedAt: r.detected_at,
      date: r.date,
      acknowledged: r.acknowledged,
    }));
    cachedSnapshot = violations;
    cachedRaw = JSON.stringify(violations);
    localStorage.setItem(STORAGE_KEY, cachedRaw);
    listeners.forEach((fn) => fn());
  } else {
    // No violations in DB — clear any leftover local data
    cachedSnapshot = [];
    cachedRaw = "[]";
    localStorage.setItem(STORAGE_KEY, "[]");
    listeners.forEach((fn) => fn());
  }
}

export function getViolations(): Violation[] {
  return readLocal();
}

export function subscribeViolations(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export async function acknowledgeViolation(id: string) {
  writeLocal(readLocal().map((v) => v.id === id ? { ...v, acknowledged: true } : v));
  if (getCurrentUserId()) {
    await dbUpdate("violations", { id }, { acknowledged: true });
  }
}

export function clearViolations() {
  writeLocal([]);
}

async function addViolation(v: Omit<Violation, "id" | "detectedAt" | "acknowledged">): Promise<Violation> {
  const detectedAt = new Date().toISOString();
  
  if (getCurrentUserId()) {
    const row = await dbInsert("violations", {
      type: v.type,
      severity: v.severity,
      trade_id: v.tradeId,
      instrument: v.instrument,
      description: v.description,
      detected_at: detectedAt,
      date: v.date,
      acknowledged: false,
    });
    if (row) {
      const violation: Violation = {
        id: row.id,
        type: v.type as ViolationType,
        severity: v.severity as any,
        tradeId: v.tradeId,
        instrument: v.instrument,
        description: v.description,
        detectedAt,
        date: v.date,
        acknowledged: false,
      };
      writeLocal([violation, ...readLocal()]);
      return violation;
    }
  }

  const violation: Violation = {
    ...v,
    id: crypto.randomUUID(),
    detectedAt,
    acknowledged: false,
  };
  writeLocal([violation, ...readLocal()]);
  return violation;
}

export async function detectViolations(trade: StoredTrade): Promise<Violation[]> {
  const allTrades = getTrades();
  const detected: Violation[] = [];
  const todayStr = new Date().toISOString().split("T")[0];
  const todayTrades = allTrades.filter((t) => t.date === todayStr);

  if (trade.emotion === "FOMO") {
    detected.push(await addViolation({
      type: "fomo", severity: "high", tradeId: trade.id, instrument: trade.instrument,
      description: `FOMO entry on ${trade.instrument} — chasing the market instead of waiting for your setup.`, date: trade.date,
    }));
  }

  if (trade.emotion === "Revenge") {
    detected.push(await addViolation({
      type: "revenge", severity: "critical", tradeId: trade.id, instrument: trade.instrument,
      description: `Revenge trade on ${trade.instrument} — trading to recover losses instead of following your plan.`, date: trade.date,
    }));
  }

  if (["Anxious", "Greedy"].includes(trade.emotion)) {
    detected.push(await addViolation({
      type: "emotional_entry", severity: "medium", tradeId: trade.id, instrument: trade.instrument,
      description: `${trade.emotion} entry on ${trade.instrument} — emotional state may have affected decision quality.`, date: trade.date,
    }));
  }

  if (todayTrades.length > 6) {
    detected.push(await addViolation({
      type: "overtrading", severity: "high", tradeId: trade.id, instrument: trade.instrument,
      description: `${todayTrades.length} trades today — overtrading detected. Quality over quantity.`, date: trade.date,
    }));
  }

  const sorted = [...allTrades].sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
  if (sorted.length >= 2) {
    const prev = sorted[1];
    if (prev.date === trade.date) {
      const [ph, pm] = prev.time.split(":").map(Number);
      const [th, tm] = trade.time.split(":").map(Number);
      const diffMin = (th * 60 + tm) - (ph * 60 + pm);
      if (diffMin >= 0 && diffMin < 5 && prev.pnl < 0) {
        detected.push(await addViolation({
          type: "revenge", severity: "high", tradeId: trade.id, instrument: trade.instrument,
          description: `Trade entered ${diffMin}m after a loss on ${prev.instrument} — possible revenge trade pattern.`, date: trade.date,
        }));
      }
    }
  }

  if (trade.rr < -2) {
    detected.push(await addViolation({
      type: "risk_exceeded", severity: "critical", tradeId: trade.id, instrument: trade.instrument,
      description: `${trade.rr.toFixed(2)}R loss on ${trade.instrument} — risk management rules breached.`, date: trade.date,
    }));
  }

  let consecutiveLosses = 0;
  for (const t of sorted) {
    if (t.pnl <= 0) consecutiveLosses++;
    else break;
  }
  if (consecutiveLosses >= 3 && ["Anxious", "FOMO", "Revenge", "Greedy"].includes(trade.emotion)) {
    detected.push(await addViolation({
      type: "emotional_entry", severity: "critical", tradeId: trade.id, instrument: trade.instrument,
      description: `${consecutiveLosses} consecutive losses with ${trade.emotion} emotion — high-risk behavioral pattern detected.`, date: trade.date,
    }));
  }

  return detected;
}

export async function logCooldownOverride(): Promise<Violation> {
  const todayStr = new Date().toISOString().split("T")[0];
  return addViolation({
    type: "overtrading",
    severity: "high",
    tradeId: null,
    instrument: "",
    description: "Cooldown override activated — consecutive loss protection was manually bypassed. Monitor emotional state closely.",
    date: todayStr,
  });
}

export function getPatternAnalysis() {
  const violations = readLocal();
  const typeCounts: Record<string, number> = {};
  const severityCounts: Record<string, number> = { low: 0, medium: 0, high: 0, critical: 0 };
  const dailyCounts: Record<string, number> = {};

  for (const v of violations) {
    typeCounts[v.type] = (typeCounts[v.type] || 0) + 1;
    severityCounts[v.severity]++;
    dailyCounts[v.date] = (dailyCounts[v.date] || 0) + 1;
  }

  const worstDay = Object.entries(dailyCounts).sort((a, b) => b[1] - a[1])[0];
  const topViolation = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0];

  return {
    total: violations.length,
    unacknowledged: violations.filter((v) => !v.acknowledged).length,
    typeCounts,
    severityCounts,
    worstDay: worstDay ? { date: worstDay[0], count: worstDay[1] } : null,
    topViolation: topViolation ? { type: topViolation[0] as ViolationType, count: topViolation[1] } : null,
  };
}
